import { Router } from '@angular/router';
import { Component, computed, inject, signal } from '@angular/core';
import { UsuarioService } from 'src/app/services/usuario.service';
import { OrdenPrestador } from 'src/app/models/OrdenPrestador';
import { AuthService } from 'src/app/services/auth.service';
import { OrderPrestadorService } from 'src/app/services/order-prestador.service';
import { OrderComercioService } from 'src/app/services/order-comercio.service';
import { OrdenComercio } from 'src/app/models/OrdenComercio';
import { API_URI } from 'src/environments/environment';
import { NotificacionService } from 'src/app/services/notificacion.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { LoadingService } from 'src/app/services/loading.service';
import { IS_NATIVE } from 'src/app/shared/platform.util';
import { Capacitor } from '@capacitor/core';

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.css'],
    standalone: false
})
export class DashboardComponent {
  private _orderPrestadorService = inject(OrderPrestadorService);
  private _orderComercioService = inject(OrderComercioService);
  _usuarioService = inject(UsuarioService);
  private _authService = inject(AuthService);
  private _notificacionService = inject(NotificacionService);
  private _loadingService = inject(LoadingService);

  // loading global del app
  loadingGlobal = computed(() => this._loadingService.isLoading());

  // loading local del dashboard (para skeletons / bloques)
  loading = signal<boolean>(true); // Indica si los datos están cargando
  asociadoCardio = computed(() => this._usuarioService.asociadoCardio());
  asociadoCardioLoading = computed(() => this._usuarioService.asociadoCardioLoading());
  error = signal<string>('');

  preview_img_avatar = '';
  nombre = '';
  id_perfil = 0;
  showList = 'especialidad';

  ordenesPrestadorDelDia: OrdenPrestador[] = [];
  ordenesPrestadorAprobar: OrdenPrestador[] = [];
  ordenesPrestador: OrdenPrestador[] = [];
  ordenesComercio: OrdenComercio[] = [];

  apiUri: string = API_URI;

  showNotifications = false;
  showCalificarOrderComercio = false;
  showCalificarOrderPrestador = false;

  showFillData = false;
  linkFillData = '/my-account-identidad';
  showFillUbicacionesData = false;
  showFillServiciosData = false;
  showFillHorariosData = false;
  showFillMercadoPagoData = false;

  fecha_actual: Date = new Date();

  // VARIABLES ADMINISTRADOR
  ingresosMesVigente = 0;
  ingresosMesAnterior = 0;
  reservasMesVigente = 0;
  reservasMesAnterior = 0;
  pedidosMesVigente = 0;
  pedidosMesAnterior = 0;
  cantidadClientesMesVigente = 0;
  cantidadClientesMesAnterior = 0;
  cantidadPrestadoresMesVigente = 0;
  cantidadPrestadoresMesAnterior = 0;
  cantidadComerciosMesVigente = 0;
  cantidadComerciosMesAnterior = 0;
  mejoresComercios: any[] = [];
  mejoresPrestadores: any[] = [];
  productosMasVendidos: any[] = [];
  vademecumsMasVendidos: any[] = [];
  prestacionesMasVendidas: any[] = [];
  ultimosClientesRegistrados: any[] = [];
  ultimosPrestadoresRegistrados: any[] = [];
  ultimosComerciosRegistrados: any[] = [];
  ordenesPorProvincias: any[] = [];
  
  now = new Date();
  firstDay = new Date(this.now.getFullYear(), this.now.getMonth(), 1);
  lastDay = new Date(this.now.getFullYear(), this.now.getMonth() + 1, 0);
  firstDayPrevMonth = new Date(this.now.getFullYear(), this.now.getMonth() - 1, 1);
  lastDayPrevMonth = new Date(this.now.getFullYear(), this.now.getMonth(), 0);

  private subscription?: Subscription;

  constructor(private router: Router) {
    this.fecha_actual.setHours(0, 0, 0, 0);
  }

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;
    this.loading.set(true);

    // 1) bootstrap de sesión con loading global
    await this._loadingService.run(async () => {
      try {
        await this._authService.ensureSession();

        if (this._authService.session() === 'guest') {
          this.router.navigate(['/sign-in']);
          return;
        }
        
        await this._usuarioService.refreshAsociadoCardio();
        
        const usuario = this._usuarioService.usuario();

        if (usuario?.id) {
          await this._notificacionService.verificarEstadoNotificaciones(usuario.id);
        }
      } catch (e: any) {
        console.error(e);
        this.error.set(e?.message || 'No se pudo cargar tu cuenta.');
        this.router.navigate(['/sign-in']);
      }
    });

    // si quedó guest y navegó, corto
    if (this._authService.session() === 'guest') return;

    try {
      this.subscription = this._notificacionService.showNotifications$.subscribe(valor => {
        this.showNotifications = valor;
      });

      const usuario = this._usuarioService.usuario();
      this.id_perfil = usuario.id_perfil ?? 0;
      this.preview_img_avatar = usuario.x_img_avatar
        ? API_URI + 'uploads/images/small-' + usuario.x_img_avatar
        : '';

      if (!usuario.id_perfil || !usuario.id) {
        this.loading.set(false);
        return;
      }

      switch (usuario.id_perfil) {
        case 1: // ADMINISTRADOR
          if (this._usuarioService.cliente().x_nombre || this._usuarioService.cliente().x_apellido) {
            this.nombre = `${this._usuarioService.cliente().x_nombre ?? ''} ${this._usuarioService.cliente().x_apellido ?? ''}`.trim();
          }
          this.checkClienteData();
          await this.loadAdminData(usuario.id);
          break;

        case 2: // CLIENTE
          if (this._usuarioService.cliente().x_nombre || this._usuarioService.cliente().x_apellido) {
            this.nombre = `${this._usuarioService.cliente().x_nombre ?? ''} ${this._usuarioService.cliente().x_apellido ?? ''}`.trim();
          }
          this.checkClienteData();
          await this.loadClienteData(usuario.id);
          break;

        case 3: // PRESTADOR
          if (this._usuarioService.prestador().x_nombre || this._usuarioService.prestador().x_apellido) {
            this.nombre = `${this._usuarioService.prestador().x_nombre ?? ''} ${this._usuarioService.prestador().x_apellido ?? ''}`.trim();
          }
          await this.checkPrestadorData();
          await this.loadPrestadorData(usuario.id);
          break;

        case 4: // COMERCIO
          if (this._usuarioService.comercio().x_nombre_fantasia) {
            this.nombre = this._usuarioService.comercio()?.x_nombre_fantasia?.trim() ?? '';
          }
          await this.checkComercioData();
          await this.loadComercioData(usuario.id);
          break;
      }
    } catch (e: any) {
      console.error(e);
      this.error.set(e?.message || 'No se pudo cargar el dashboard.');
    } finally {
      this.loading.set(false);
    }
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  private async loadAdminData(userId: string): Promise<void> {
    const [resOrdCome, resOrdPres, resDashboard] = await Promise.all([
      firstValueFrom(this._orderComercioService.getOrdenesComercioXComercio(userId)),
      firstValueFrom(this._orderPrestadorService.getOrdenesPrestadorXPrestador(userId)),
      firstValueFrom(this._usuarioService.getDashboard(this.firstDay, this.lastDay)),
    ]);

    this.ordenesComercio = (resOrdCome ?? []).filter(x =>
      x.id_estado_orden == 1 || x.id_estado_orden == 2 || x.id_estado_orden == 3 ||
      x.id_estado_orden == 6 || x.id_estado_orden == 7 || x.id_estado_orden == 8 ||
      x.id_estado_orden == 11
    );
    this.showCalificarOrderComercio = (resOrdCome ?? []).some(x => x.id_estado_orden == 4);

    this.ordenesPrestador = (resOrdPres ?? []).filter(x =>
      x.id_estado_orden == 1 || x.id_estado_orden == 2 || x.id_estado_orden == 3 ||
      x.id_estado_orden == 6 || x.id_estado_orden == 7 || x.id_estado_orden == 8 ||
      x.id_estado_orden == 11
    );
    this.showCalificarOrderPrestador = (resOrdPres ?? []).some(x => x.id_estado_orden == 4);

    this.ingresosMesVigente = resDashboard?.monto_cobrado_mes_vigente ?? 0;
    this.ingresosMesAnterior = resDashboard?.monto_cobrado_mes_anterior ?? 0;
    this.reservasMesVigente = resDashboard?.ordenes_prestador_mes_vigente ?? 0;
    this.reservasMesAnterior = resDashboard?.ordenes_prestador_mes_anterior ?? 0;
    this.pedidosMesVigente = resDashboard?.ordenes_comercio_mes_vigente ?? 0;
    this.pedidosMesAnterior = resDashboard?.ordenes_comercio_mes_anterior ?? 0;
    this.cantidadClientesMesVigente = resDashboard?.clientes_registrados_mes_vigente ?? 0;
    this.cantidadClientesMesAnterior = resDashboard?.clientes_registrados_mes_anterior ?? 0;
    this.cantidadComerciosMesVigente = resDashboard?.comercios_registrados_mes_vigente ?? 0;
    this.cantidadComerciosMesAnterior = resDashboard?.comercios_registrados_mes_anterior ?? 0;
    this.cantidadPrestadoresMesVigente = resDashboard?.prestadores_registrados_mes_vigente ?? 0;
    this.cantidadPrestadoresMesAnterior = resDashboard?.prestadores_registrados_mes_anterior ?? 0;
    this.mejoresComercios = resDashboard?.mejores_comercios ?? [];
    this.mejoresPrestadores = resDashboard?.mejores_prestadores ?? [];
    this.productosMasVendidos = resDashboard?.productos_mas_vendidos ?? [];
    this.vademecumsMasVendidos = resDashboard?.vademecums_mas_vendidos ?? [];
    this.prestacionesMasVendidas = resDashboard?.prestaciones_mas_pedidas ?? [];
    this.ultimosClientesRegistrados = resDashboard?.ultimos_clientes ?? [];
    this.ultimosComerciosRegistrados = resDashboard?.ultimos_comercios ?? [];
    this.ultimosPrestadoresRegistrados = resDashboard?.ultimos_prestadores ?? [];
    this.ordenesPorProvincias = resDashboard?.ordenes_por_provincias ?? [];
  }

  private async loadClienteData(userId: string): Promise<void> {
    const [resOrdCome, resOrdPres] = await Promise.all([
      firstValueFrom(this._orderComercioService.getOrdenesComercioXUsuario(userId)),
      firstValueFrom(this._orderPrestadorService.getOrdenesPrestadorXUsuario(userId)),
    ]);

    this.ordenesComercio = (resOrdCome ?? []).filter(x =>
      x.id_estado_orden == 1 || x.id_estado_orden == 2 || x.id_estado_orden == 3 ||
      x.id_estado_orden == 6 || x.id_estado_orden == 7 || x.id_estado_orden == 8 ||
      x.id_estado_orden == 11
    );
    this.showCalificarOrderComercio = (resOrdCome ?? []).some(x => x.id_estado_orden == 4);

    this.ordenesPrestador = (resOrdPres ?? []).filter(x =>
      x.id_estado_orden == 1 || x.id_estado_orden == 2 || x.id_estado_orden == 3 ||
      x.id_estado_orden == 6 || x.id_estado_orden == 7 || x.id_estado_orden == 8 ||
      x.id_estado_orden == 11
    );
    this.showCalificarOrderPrestador = (resOrdPres ?? []).some(x => x.id_estado_orden == 4);
  }

  private async loadPrestadorData(userId: string): Promise<void> {
    const [resOrdCome, resOrdPres] = await Promise.all([
      firstValueFrom(this._orderComercioService.getOrdenesComercioXUsuario(userId)),
      firstValueFrom(this._orderPrestadorService.getOrdenesPrestadorXPrestador(userId)),
    ]);

    this.ordenesComercio = (resOrdCome ?? []).filter(x =>
      x.id_estado_orden == 1 || x.id_estado_orden == 2 || x.id_estado_orden == 3 ||
      x.id_estado_orden == 6 || x.id_estado_orden == 7 || x.id_estado_orden == 8 ||
      x.id_estado_orden == 11
    );
    this.showCalificarOrderComercio = (resOrdCome ?? []).some(x => x.id_estado_orden == 4);

    this.ordenesPrestadorDelDia = (resOrdPres ?? []).filter(x => x.id_estado_orden == 6);
    this.ordenesPrestadorAprobar = (resOrdPres ?? []).filter(x =>
      x.id_estado_orden == 1 || x.id_estado_orden == 2 || x.id_estado_orden == 3 ||
      x.id_estado_orden == 7 || x.id_estado_orden == 8 || x.id_estado_orden == 11
    );
    this.showCalificarOrderPrestador = (resOrdPres ?? []).some(x => x.id_estado_orden == 4);
  }

  private async loadComercioData(userId: string): Promise<void> {
    const [resOrdCome, resOrdPres] = await Promise.all([
      firstValueFrom(this._orderComercioService.getOrdenesComercioXComercio(userId)),
      firstValueFrom(this._orderPrestadorService.getOrdenesPrestadorXPrestador(userId)),
    ]);

    this.ordenesComercio = (resOrdCome ?? []).filter(x =>
      x.id_estado_orden == 1 || x.id_estado_orden == 2 || x.id_estado_orden == 3 ||
      x.id_estado_orden == 6 || x.id_estado_orden == 7 || x.id_estado_orden == 8 ||
      x.id_estado_orden == 11
    );
    this.showCalificarOrderComercio = (resOrdCome ?? []).some(x => x.id_estado_orden == 4);

    this.ordenesPrestador = (resOrdPres ?? []).filter(x =>
      x.id_estado_orden == 1 || x.id_estado_orden == 2 || x.id_estado_orden == 3 ||
      x.id_estado_orden == 6 || x.id_estado_orden == 7 || x.id_estado_orden == 8 ||
      x.id_estado_orden == 11
    );
    this.showCalificarOrderPrestador = (resOrdPres ?? []).some(x => x.id_estado_orden == 4);
  }

  calcularCrecimiento(valorMesVigente: number, valorMesAnterior: number): number {
    if (valorMesAnterior === 0) return valorMesVigente > 0 ? 100 : 0;
    return ((valorMesVigente - valorMesAnterior) / valorMesAnterior) * 100;
  }

  selectFilter(option: string) {
    this.showList = option;
  }

  async subscribeToNotifications() {
    try {
      this.loading.set(true);
      this.error.set('');

      // Listeners ANTES de register() para no perder el evento 'registration' por race condition
      if (IS_NATIVE) {
        await this._notificacionService.setupNotificationListeners();
      }

      if (!await this._notificacionService.registerForPushNotifications()) {
        this.loading.set(false);
        this.error.set('Debe dar permisos para recibir notificaciones');
        console.error('Error al registrar las notificaciones');
        return;
      }

      setTimeout(() => {
        this._notificacionService.setShowNotifications(false);
        this.loading.set(false);
      }, 2500);
    } catch (error) {
      this.loading.set(false);
      this.error.set('Error al suscribirse a las notificaciones');
      console.error('Error al suscribirse a las notificaciones:', error);
    }
  }

  cedEvent(event: any) {
    // reservado
  }

  checkClienteData() {
    if (
      !this._usuarioService.cliente().x_nombre ||
      !this._usuarioService.cliente().x_apellido ||
      !this._usuarioService.usuario().id_tipo_documento ||
      !this._usuarioService.usuario().x_nro_documento
    ) {
      this.linkFillData = '/my-account-identidad';
      this.showFillData = true;
    } else {
      this.showFillData = false;
    }
  }

  async checkComercioData() {
    if (
      !this._usuarioService.comercio().x_razon_social ||
      !this._usuarioService.comercio().x_nombre_fantasia ||
      !this._usuarioService.usuario().id_tipo_documento ||
      !this._usuarioService.usuario().x_nro_documento
    ) {
      this.linkFillData = '/my-account-identidad-comercio';
      this.showFillData = true;
    } else {
      this.showFillData = false;
    }

    try {
      const [ubicaciones, horario] = await Promise.all([
        firstValueFrom(this._usuarioService.getUbicaciones(this._usuarioService.comercio().id!, true)),
        firstValueFrom(this._usuarioService.getHorario(this._usuarioService.comercio().id!, true)),
      ]);

      this.showFillUbicacionesData = !(ubicaciones?.length > 0);

      this.showFillHorariosData = !horario ||
        horario.x_lunes_desde == '' || horario.x_lunes_hasta == '' ||
        horario.x_martes_desde == '' || horario.x_martes_hasta == '' ||
        horario.x_miercoles_desde == '' || horario.x_miercoles_hasta == '' ||
        horario.x_jueves_desde == '' || horario.x_jueves_hasta == '';
    } catch {
      this.showFillUbicacionesData = true;
      this.showFillHorariosData = true;
    }

    this.showFillMercadoPagoData = !(
      this._usuarioService.usuario().x_mp_access_token &&
      (this.fecha_actual < new Date(this._usuarioService.usuario().d_mp_expires_in!))
    );
  }

  async checkPrestadorData() {
    if (
      this._usuarioService.prestador().x_nombre == '' ||
      !this._usuarioService.prestador().x_apellido ||
      !this._usuarioService.usuario().id_tipo_documento ||
      !this._usuarioService.usuario().x_nro_documento ||
      !this._usuarioService.prestador().x_nro_matricula
    ) {
      this.linkFillData = '/my-account-identidad-prestador';
      this.showFillData = true;
    } else {
      this.showFillData = false;
    }

    try {
      const [servicios, ubicaciones] = await Promise.all([
        firstValueFrom(this._usuarioService.getServicios(this._usuarioService.prestador().id, true)),
        firstValueFrom(this._usuarioService.getUbicaciones(this._usuarioService.prestador().id!, true)),
      ]);

      this.showFillServiciosData = !(servicios?.length > 0);
      this.showFillUbicacionesData = !(ubicaciones?.length > 0);
    } catch {
      this.showFillServiciosData = true;
      this.showFillUbicacionesData = true;
    }

    this.showFillMercadoPagoData = !(
      this._usuarioService.usuario().x_mp_access_token &&
      (this.fecha_actual < new Date(this._usuarioService.usuario().d_mp_expires_in!))
    );
  }

  getEstadoImagen(id_estado_orden: number): string {
    switch (id_estado_orden) {
      case 1: // INICIADO
        return './assets/img/estado-1-1.png';
      case 2: // RESPONDIDO
        return './assets/img/estado-1-1.png';
      case 3: // ACEPTADO
        return './assets/img/estado-1-2.png';
      case 4: // COMPLETADO
        return './assets/img/estado-1-3.png';
      case 5: // CANCELADO
        return './assets/img/estado-1-5.png';
      case 6: // PAGO APROBADO
        return './assets/img/estado-1-3.png';
      case 7: // PROCESANDO PAGO
        return './assets/img/estado-1-5.png';
      case 8: // PAGO RECHAZADO
        return './assets/img/estado-1-5.png';
      case 9: // CALIFICADO
        return './assets/img/estado-1-4.png';
      case 10: // DESETIMADO
        return './assets/img/estado-1-5.png';
      case 11: // PAGO CANCELADO
        return './assets/img/estado-1-5.png';
      default:
        return './assets/img/estado-1-1.png';
    }
  }

  openCardioWeb(): void {
    window.open('https://www.cardio.ar/', '_blank');
  }

  openCardioApp(): void {
    const isIos = Capacitor.getPlatform() === 'ios';
    const isAndroid = Capacitor.getPlatform() === 'android';

    if (isIos) {
      window.open(
        'https://apps.apple.com/ar/app/cardio-24hs/id6752791637',
        '_blank'
      );
      return;
    }

    if (isAndroid) {
      window.open(
        'https://play.google.com/store/apps/details?id=com.drimfix.cardio&hl=es_AR&pli=1',
        '_blank'
      );
      return;
    }

    // fallback web
    window.open(
      'https://play.google.com/store/apps/details?id=com.drimfix.cardio',
      '_blank'
    );
  }

  imgFallback(event: Event, fallback: string): void {
    const img = event.target as HTMLImageElement;

    if (!img.src.includes(fallback)) {
      img.src = fallback;
    }
  }
}
